import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Stack,
  Checkbox,
  Skeleton,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Visibility,
  RefreshOutlined,
  WarningAmber,
  OpenInNew,
} from "@mui/icons-material";
import { productsService } from "../../../services/productsService";
import { directSalesService } from "../../../services/directSalesService";
import { useAuth } from "../../../context/context";
import { pendingChangesService } from "../../../services/pendingChangesService";
import { buildAdminLotUrl } from "../../../config/environment";

const STATUS_COLORS = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  stale: "warning",
  superseded: "default",
  cancelled: "default",
};

const ENTITY_LABELS = {
  DirectSale: "Venta Directa",
  DirectSalePriceDetails: "Detalles de Precio",
  Lot: "Lote",
};

/**
 * Aliases legibles para los campos del whitelist 4-eyes.
 * Solo aplican a Venta Directa Bienes (que es donde existe este flujo).
 */
const FIELD_LABELS = {
  // Lot scalars
  price: "Valor del lote",
  commission: "Comisión Comprador",
  sellerCommission: "Comisión Vendedor",
  ivaCode: "Tipo de IVA",
  // DirectSalePriceDetails (campos PLANOS — pendings de tipo DirectSalePriceDetails)
  iva: "Detalle Precio: IVA (Afecto/Exento)",
  active: "Detalle Precio: Detalle Activo",
  ownerPrice: "Detalle Precio: Valor Mandante",
  ownerPriceVat: "Detalle Precio: IVA Mandante",
  commissionPercentage: "Detalle Precio: Comisión Tattersall",
  operationalCosts: "Detalle Precio: Gastos Operacionales",
  operationalCostsVat: "Detalle Precio: IVA Gastos Operacionales",
  netPriceWithVat: "Detalle Precio: Total (Neto + IVAs)",
  transferFee: "Detalle Precio: Costo Transferencia",
  transferTax: "Detalle Precio: Impuesto Transferencia",
  taxAppraisal: "Detalle Precio: Tasación Fiscal",
  additionalCosts: "Detalle Precio: Costos Adicionales",
  hasLicensePlate: "Detalle Precio: Tiene Patente",
  vatPercentage: "Detalle Precio: IVA",
  taxType: "Detalle Precio: Tipo de Impuesto",
  bankType: "Detalle Precio: Tipo de Banco",
  usageType: "Detalle Precio: Tipo de Uso",
  // PriceDetails (namespaced)
  "priceDetails.ownerPrice": "Detalle Precio: Valor Mandante",
  "priceDetails.ownerPriceVat": "Detalle Precio: IVA Mandante",
  "priceDetails.commissionPercentage": "Detalle Precio: Comisión Tattersall",
  "priceDetails.operationalCosts": "Detalle Precio: Gastos Operacionales",
  "priceDetails.operationalCostsVat":
    "Detalle Precio: IVA Gastos Operacionales",
  "priceDetails.netPriceWithVat": "Detalle Precio: Total (Neto + IVAs)",
  "priceDetails.transferFee": "Detalle Precio: Costo Transferencia",
  "priceDetails.transferTax": "Detalle Precio: Impuesto Transferencia",
  "priceDetails.additionalCosts": "Detalle Precio: Costos Adicionales",
  "priceDetails.taxType": "Detalle Precio: Tipo de Impuesto",
  "priceDetails.bankType": "Detalle Precio: Tipo de Banco",
  "priceDetails.hasLicensePlate": "Detalle Precio: Tiene Patente",
  "priceDetails.iva": "Detalle Precio: IVA (Afecto/Exento)",
  "priceDetails.usageType": "Detalle Precio: Tipo de Uso",
  "priceDetails.vatPercentage": "Detalle Precio: IVA",
  "priceDetails.transferTaxPercentage":
    "Detalle Precio: Impuesto Transferencia",
  "priceDetails.transferMultiplier":
    "Detalle Precio: Multiplicador Transferencia",
  // DirectSale
  finalPrice: "Precio Final",
  buyerId: "Comprador",
  sellerId: "Vendedor",
};

/** Campos que se muestran como monto en pesos. */
const CURRENCY_FIELDS = new Set([
  "price",
  "finalPrice",
  // PriceDetails planos
  "ownerPrice",
  "ownerPriceVat",
  "operationalCosts",
  "operationalCostsVat",
  "netPriceWithVat",
  "transferFee",
  "transferTax",
  // PriceDetails namespaced
  "priceDetails.ownerPrice",
  "priceDetails.ownerPriceVat",
  "priceDetails.operationalCosts",
  "priceDetails.operationalCostsVat",
  "priceDetails.netPriceWithVat",
  "priceDetails.transferFee",
  "priceDetails.transferTax",
]);

/** Campos que se muestran como porcentaje. */
const PERCENT_FIELDS = new Set([
  "commission",
  "sellerCommission",
  // PriceDetails planos
  "commissionPercentage",
  "vatPercentage",
  // PriceDetails namespaced
  "priceDetails.commissionPercentage",
  "priceDetails.vatPercentage",
  "priceDetails.transferTaxPercentage",
]);

/** Campos que representan la lista de costos adicionales. */
const ADDITIONAL_COSTS_FIELDS = new Set([
  "additionalCosts",
  "priceDetails.additionalCosts",
]);

const labelFor = (field) => FIELD_LABELS[field] || field;

const numberFormatter = new Intl.NumberFormat("es-CL");

const formatNumberCL = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n ?? "—");
  return numberFormatter.format(Math.round(num * 100) / 100);
};

const formatCurrency = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `$ ${numberFormatter.format(Math.round(num))}`;
};

const formatPercent = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `${numberFormatter.format(num)} %`;
};

const formatBoolean = (v) => (v ? "Sí" : "No");

/** Renderer enriquecido por campo. Devuelve string o ReactNode. */
const renderFieldValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "—";

  // Costos adicionales: lista enriquecida.
  if (ADDITIONAL_COSTS_FIELDS.has(field) && Array.isArray(value)) {
    if (value.length === 0) return "Sin costos adicionales";
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {value.map((cost, idx) => (
          <li key={idx}>
            <strong>{cost?.name ?? "—"}</strong>: {formatCurrency(cost?.value)}
            {cost?.description ? ` — ${cost.description}` : ""}
          </li>
        ))}
      </Box>
    );
  }

  // Booleans (hasLicensePlate, etc.)
  if (typeof value === "boolean") return formatBoolean(value);

  // Currency
  if (CURRENCY_FIELDS.has(field)) return formatCurrency(value);

  // Percent
  if (PERCENT_FIELDS.has(field)) return formatPercent(value);

  // Numérico genérico (pero no string que parece nombre)
  if (typeof value === "number") return formatNumberCL(value);

  // Object/array fallback compacto
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

const formatDate = (s) => (s ? new Date(s).toLocaleString("es-CL") : "—");

/**
 * Modal de revisión: muestra el diff y permite aprobar/rechazar.
 * Si hay conflicto (snapshot ≠ valor actual), muestra tres columnas.
 */
const PendingChangeReviewModal = ({
  open,
  pendingId,
  entityType,
  entityId,
  onClose,
  onActionDone,
}) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [productInfo, setProductInfo] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productHidden, setProductHidden] = useState(false);

  const load = useCallback(async () => {
    if (!pendingId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await pendingChangesService.getById(pendingId, token);
      setData(res.data);
      // Por defecto marcamos todos los campos para aplicar.
      const allFields = Object.keys(res.data?.pending?.changes || {});
      setSelectedFields(allFields);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pendingId, token]);

  const fetchProductInfo = useCallback(async () => {
    if (!entityId || !entityType) return;
    setProductLoading(true);
    setProductInfo(null);
    setProductHidden(false);
    try {
      if (entityType === "Lot" || entityType === "DirectSalePriceDetails") {
        const product = await productsService.getProductById(entityId, token);
        setProductInfo(product);
      } else if (entityType === "DirectSale") {
        const saleResp = await directSalesService.getDirectSaleById(
          entityId,
          token,
        );
        const saleData = saleResp?.data || saleResp;
        const lot = saleData?.lot;
        if (lot) {
          setProductInfo(lot);
        } else if (saleData?.lotId) {
          const product = await productsService.getProductById(
            saleData.lotId,
            token,
          );
          setProductInfo(product);
        }
      }
    } catch (e) {
      const msg = e.message || "";
      const isNotFound =
        msg.includes("status: 404") ||
        msg.includes("PRODUCT_NOT_FOUND") ||
        msg.toLowerCase().includes("no encontrado") ||
        msg.toLowerCase().includes("not found");
      if (isNotFound) {
        setProductHidden(true);
      } else {
        console.warn("Could not load product info for pending change:", e);
      }
    } finally {
      setProductLoading(false);
    }
  }, [entityType, entityId, token]);

  useEffect(() => {
    if (open) {
      setNotes("");
      setSelectedFields([]);
      load();
      fetchProductInfo();
    }
  }, [open, load, fetchProductInfo]);

  if (!open) return null;

  const pending = data?.pending;
  const currentValues = data?.currentValues || {};
  const hasConflict = !!data?.hasConflict;
  const isOwn = pending?.requester.email === user?.email;
  const canReview = pending?.status === "pending" && !isOwn;
  const handleApprove = async () => {
    if (selectedFields.length === 0) {
      setError("Debes seleccionar al menos un campo para aplicar");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await pendingChangesService.approve(
        pendingId,
        notes || null,
        token,
        selectedFields,
      );
      onActionDone?.("approved");
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleField = (field) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  };

  const toggleAllFields = () => {
    const allFields = Object.keys(data?.pending?.changes || {});
    if (selectedFields.length === allFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(allFields);
    }
  };

  const handleReject = async () => {
    if (!notes.trim()) {
      setError("Debes indicar el motivo del rechazo");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await pendingChangesService.reject(pendingId, notes.trim(), token);
      onActionDone?.("rejected");
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldRows = pending
    ? Object.entries(pending.changes || {}).map(([field, diff]) => {
        const current = currentValues[field];
        const conflict = JSON.stringify(diff.old) !== JSON.stringify(current);
        return { field, old: diff.old, proposed: diff.new, current, conflict };
      })
    : [];

  // En pendings ya aprobados, distinguimos qué campos se aplicaron vs descartaron.
  // `appliedFields` es null en pendings históricos previos a esta feature:
  // en ese caso asumimos que se aplicaron todos (compatibilidad hacia atrás).
  const isApproved = pending?.status === "approved";
  const appliedFieldsList = pending?.appliedFields ?? null;
  const wasFieldApplied = (field) =>
    appliedFieldsList === null ? true : appliedFieldsList.includes(field);

  const productLotId = entityType === "DirectSale" ? productInfo?.id : entityId;
  const productImage =
    productInfo?.images?.[0]?.url ||
    productInfo?.images?.[0] ||
    productInfo?.image ||
    null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Revisar cambio pendiente #{pendingId}
        {pending && (
          <Chip
            sx={{ ml: 2 }}
            size="small"
            label={pending.status}
            color={STATUS_COLORS[pending.status] || "default"}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {/* Tarjeta de producto — carga en paralelo con el modal */}
        {(productLoading || productInfo || productHidden) && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              mb: 2,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            {productLoading ? (
              <>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={80}
                  sx={{ borderRadius: 1, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" height={24} />
                  <Skeleton width="35%" height={18} sx={{ mt: 0.5 }} />
                  <Skeleton width="45%" height={18} sx={{ mt: 0.5 }} />
                </Box>
                <Skeleton
                  variant="rectangular"
                  width={110}
                  height={34}
                  sx={{ borderRadius: 1, flexShrink: 0 }}
                />
              </>
            ) : productHidden ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flex: 1,
                }}
              >
                <WarningAmber sx={{ color: "warning.main", flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" color="warning.dark">
                    Lote ocultado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Este lote (#{entityId}) fue ocultado y no está disponible en
                    Venta Usados.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                {productImage ? (
                  <Box
                    component="img"
                    src={productImage}
                    alt={productInfo.name}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 1,
                      flexShrink: 0,
                      bgcolor: "grey.100",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 1,
                      flexShrink: 0,
                      bgcolor: "grey.200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.disabled">
                      Sin imagen
                    </Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    noWrap
                    title={productInfo.name}
                  >
                    {productInfo.name || "—"}
                  </Typography>
                  {productLotId && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      ID Lote: #{productLotId}
                    </Typography>
                  )}
                  {productInfo.price != null && (
                    <Typography
                      variant="body2"
                      color="primary.main"
                      fontWeight={600}
                    >
                      {formatCurrency(productInfo.price)}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {productInfo.slug && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNew />}
                      component="a"
                      href={`/producto/${productInfo.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ flexShrink: 0 }}
                    >
                      Ir al ítem
                    </Button>
                  )}
                  {productLotId && token && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<OpenInNew />}
                      onClick={() => {
                        const url = buildAdminLotUrl(productLotId, token);
                        if (url)
                          window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      Editar en administrador
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : pending ? (
          <>
            <Stack
              spacing={1}
              sx={{ mb: 2, wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              <Typography variant="body2">
                <strong>Tipo:</strong>{" "}
                {ENTITY_LABELS[pending.entityType] || pending.entityType} (#
                {pending.entityId})
              </Typography>
              <Typography variant="body2">
                <strong>Solicitado por:</strong>{" "}
                {pending.requester?.email || pending.requestedBy} •{" "}
                {formatDate(pending.requestedAt)}
              </Typography>
              {pending.requestReason && (
                <Typography variant="body2">
                  <strong>Motivo:</strong> {pending.requestReason}
                </Typography>
              )}
              {pending.reviewedAt && (
                <Typography variant="body2">
                  <strong>Revisado por:</strong>{" "}
                  {pending.reviewer?.email || pending.reviewedBy} •{" "}
                  {formatDate(pending.reviewedAt)}
                </Typography>
              )}
              {pending.reviewNotes && (
                <Typography variant="body2">
                  <strong>Notas de revisión:</strong> {pending.reviewNotes}
                </Typography>
              )}
            </Stack>

            {hasConflict && (
              <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
                El valor actual de la entidad difiere del original capturado al
                solicitar el cambio. Si apruebas, el sistema marcará el cambio
                como obsoleto y NO lo aplicará.
              </Alert>
            )}
            {isOwn && pending.status === "pending" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Este cambio lo solicitaste tú. Debe revisarlo otro usuario
                (regla 4-ojos).
              </Alert>
            )}

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {canReview && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={
                            fieldRows.length > 0 &&
                            selectedFields.length === fieldRows.length
                          }
                          indeterminate={
                            selectedFields.length > 0 &&
                            selectedFields.length < fieldRows.length
                          }
                          onChange={toggleAllFields}
                        />
                      </TableCell>
                    )}
                    <TableCell>Campo</TableCell>
                    <TableCell>Original (al solicitar)</TableCell>
                    {hasConflict && <TableCell>Actual en BD</TableCell>}
                    <TableCell>Propuesto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldRows.map((r) => {
                    const discarded = isApproved && !wasFieldApplied(r.field);
                    const discardedSx = discarded
                      ? {
                          color: "text.disabled",
                          textDecoration: "line-through",
                        }
                      : null;
                    return (
                      <TableRow
                        key={r.field}
                        sx={discarded ? { opacity: 0.6 } : undefined}
                      >
                        {canReview && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedFields.includes(r.field)}
                              onChange={() => toggleField(r.field)}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={discardedSx}>
                          <strong>{labelFor(r.field)}</strong>
                        </TableCell>
                        <TableCell sx={discardedSx}>
                          {renderFieldValue(r.field, r.old)}
                        </TableCell>
                        {hasConflict && (
                          <TableCell
                            sx={{
                              color: r.conflict ? "warning.main" : "inherit",
                              fontWeight: r.conflict ? 600 : 400,
                            }}
                          >
                            {renderFieldValue(r.field, r.current)}
                            {r.conflict && " ⚠"}
                          </TableCell>
                        )}
                        <TableCell
                          sx={
                            discarded ? discardedSx : { color: "primary.main" }
                          }
                        >
                          {renderFieldValue(r.field, r.proposed)}
                          {discarded && (
                            <Chip
                              size="small"
                              label="Descartado"
                              sx={{ ml: 1 }}
                            />
                          )}
                          {isApproved && wasFieldApplied(r.field) && (
                            <Chip
                              size="small"
                              color="success"
                              label="Aplicado"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {canReview && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Solo se aplicarán los campos marcados. Los campos no marcados
                quedarán <strong>descartados</strong> y no se aplicarán nunca
                (el pending pasa a estado <code>approved</code>).
              </Alert>
            )}

            {canReview && (
              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Notas de la revisión (obligatorio para rechazar)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
              />
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        {!isOwn ? (
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cerrar
            </Button>
            {canReview && (
              <>
                <Button
                  onClick={handleReject}
                  color="error"
                  startIcon={<Cancel />}
                  disabled={submitting}
                >
                  Rechazar
                </Button>
                <Button
                  onClick={handleApprove}
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircle />}
                  disabled={submitting || selectedFields.length === 0}
                >
                  Aprobar ({selectedFields.length})
                </Button>
              </>
            )}
          </>
        ) : (
          //Explicar al usuario por qué no puede aprobar/rechazar su propio cambio
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cerrar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const PendingChangesList = () => {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
  });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const res = await pendingChangesService.list(
          {
            status: statusFilter || undefined,
            entityType: entityTypeFilter || undefined,
            page,
            limit: 20,
          },
          token,
        );
        setItems(res.items || []);
        setPagination(res.pagination || { total: 0, page: 1, limit: 20 });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [token, statusFilter, entityTypeFilter],
  );

  useEffect(() => {
    if (token) load(1);
  }, [token, load]);

  const handleActionDone = (action) => {
    setSnackbar({
      open: true,
      message:
        action === "approved"
          ? "Cambio aprobado y aplicado"
          : "Cambio rechazado",
      severity: action === "approved" ? "success" : "info",
    });
    load(pagination.page || 1);
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Cambios pendientes de aprobación
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="pending">Pendientes</MenuItem>
              <MenuItem value="approved">Aprobados</MenuItem>
              <MenuItem value="rejected">Rechazados</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="DirectSale">Venta Directa</MenuItem>
              <MenuItem value="DirectSalePriceDetails">
                Detalles de Precio
              </MenuItem>
              <MenuItem value="Lot">Lote</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Recargar">
            <IconButton onClick={() => load(pagination.page || 1)}>
              <RefreshOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Entidad</TableCell>
              <TableCell>Campos</TableCell>
              <TableCell>Solicitante</TableCell>
              <TableCell>Supervisor</TableCell>
              <TableCell>Solicitado</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay cambios para mostrar
                </TableCell>
              </TableRow>
            ) : (
              items.map((it) => {
                const isOwn =
                  it.requestedBy === user?.id || it.reviewedBy === user?.id;
                const fieldKeys = Object.keys(it.changes || {});
                const fieldLabels = fieldKeys
                  .map((f) => labelFor(f))
                  .join(", ");
                const count = fieldKeys.length;
                return (
                  <TableRow key={it.id} hover>
                    <TableCell>{it.id}</TableCell>
                    <TableCell>
                      {ENTITY_LABELS[it.entityType] || it.entityType}
                    </TableCell>
                    <TableCell>#{it.entityId}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Tooltip title={fieldLabels} placement="top" arrow>
                        <Chip
                          size="small"
                          label={`${count} ${count === 1 ? "campo" : "campos"}`}
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {it.requester?.email || it.requestedBy}
                      {isOwn && <Chip label="tú" size="small" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      {it.reviewer?.email || it.reviewedBy || "—"}
                      {isOwn && <Chip label="tú" size="small" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{formatDate(it.requestedAt)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={it.status}
                        color={STATUS_COLORS[it.status] || "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Ver detalle / revisar">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedItem(it)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Total: {pagination.total}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            disabled={loading || (pagination.page || 1) <= 1}
            onClick={() => load((pagination.page || 1) - 1)}
          >
            Anterior
          </Button>
          <Button
            size="small"
            disabled={
              loading || (pagination.page || 1) >= (pagination.totalPages || 1)
            }
            onClick={() => load((pagination.page || 1) + 1)}
          >
            Siguiente
          </Button>
        </Stack>
      </Box>

      <PendingChangeReviewModal
        open={!!selectedItem}
        pendingId={selectedItem?.id}
        entityType={selectedItem?.entityType}
        entityId={selectedItem?.entityId}
        onClose={() => setSelectedItem(null)}
        onActionDone={handleActionDone}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingChangesList;
